import { useEffect, useRef } from "react";

export interface UseExitIntentOptions {
  threshold?: number; // px from top of window
  delay?: number; // ms before exit intent activates
  once?: boolean; // only fire once per session
  enabled?: boolean;
}

export const useExitIntent = (
  callback: () => void,
  options: UseExitIntentOptions = {}
): void => {
  const {
    threshold = 20,
    delay = 3000,
    once = true,
    enabled = true,
  } = options;

  const fired = useRef<boolean>(false);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const activated = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
      return;
    }

    // Check if already shown this session
    if (sessionStorage.getItem("exit_intent_shown") === "true") {
      return;
    }

    // Activate after delay
    timer.current = setTimeout(() => {
      activated.current = true;
    }, delay);

    const handleMouseLeave = (e: MouseEvent) => {
      if (!activated.current || fired.current || e.clientY > threshold) return;

      fired.current = true;
      if (once) {
        sessionStorage.setItem("exit_intent_shown", "true");
      }
      callback();
    };

    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [callback, threshold, delay, once, enabled]);
};

